import React, { useEffect } from "react";

/**
 * @param ref a ref to the object that you click outside of and want an action performed
 * @param onClickOutside the action you would like to happen when you click
 *                       outside of the desired component in the form of a function
 * @param visibilityState Whether the ref is visible or active.  This is to prevent the app
 *                        from trying to perform a hide, for example, on an object that is
 *                        already hidden.  Pass `true` if it is always visible.
 * @param ignoreSelectors Optional CSS selectors; if the click target matches (via closest), do not call onClickOutside.
 * @returns void
 */
function useOutsideClick(
  ref: React.MutableRefObject<any>,
  onClickOutside: () => void,
  visibilityState: boolean,
  ignoreSelectors?: string[],
) {
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (visibilityState !== true || !ref.current || ref.current.contains(event.target)) return;
      if (ignoreSelectors?.some((sel) => event.target.closest?.(sel))) return;
      onClickOutside();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, visibilityState, ignoreSelectors, onClickOutside]);
}

export default useOutsideClick;
